
export default function RefinanceCTASection() {
  return (
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-12 sm:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 px-2">
            Ready to Start Your Refinance Journey?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-red-100 max-w-2xl mx-auto px-4">
            Book a free consultation to discover how much you could save with better terms.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-start">
          {/* Left Column - Calendly Widget */}
          <div className="space-y-6 order-2 lg:order-1">
            <div className="bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-700">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 text-center">
                Schedule Your Free Review
              </h3>
              <div className="bg-gray-900 border border-gray-600 rounded-xl p-2">
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro"
                  style={{
                    position: 'relative',
                    minWidth: '280px',
                    height: '500px',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                  data-processed="true"
                >
                  <div className="calendly-spinner">
                    <div className="calendly-bounce1"></div>
                    <div className="calendly-bounce2"></div>
                    <div className="calendly-bounce3"></div>  
                  </div>
                  <iframe
                    src="https://calendly.com/tjcmonteiro/free-mortgage-consultation-with-tony-monteiro?embed_domain=readdy.ai&embed_type=Inline"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Select a Date & Time - Calendly"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Benefits and Contact */}
          <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
            {/* What You'll Get */}
            <div className="bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-700">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6">
                What You'll Get in Your Review
              </h3>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-calculator-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Savings Analysis</h5>
                    <p className="text-gray-300 text-sm">See exactly how you could save monthly and over the life of your loan with current rates.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-search-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Rate Comparison</h5>
                    <p className="text-gray-300 text-sm">Compare your current rate with today's best available options from multiple lenders.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Custom Strategy</h5>
                    <p className="text-gray-300 text-sm">Get a personalized refinancing strategy based on your unique financial situation and goals.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-lg sm:text-xl"></i>
                  </div>
                  <div>
                    <h5 className="font-bold text-base sm:text-lg text-white mb-1 sm:mb-2">Timeline & Process</h5>
                    <p className="text-gray-300 text-sm">Clear roadmap of next steps and realistic timeline for completing your refinance.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-gray-800 rounded-2xl p-4 sm:p-6 border border-gray-700">
              <h4 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Prefer to Talk First?</h4>
              <div className="space-y-2 sm:space-y-3">
                <a 
                  href="tel:5196583026" 
                  className="flex items-center space-x-3 text-red-400 hover:text-red-300 font-semibold text-base sm:text-lg transition-colors"
                >
                  <i className="ri-phone-line text-lg sm:text-xl"></i>
                  <span>(519) 658-3026</span>
                </a>
                <p className="text-gray-400 text-xs sm:text-sm">Available Mon-Fri 9AM-7PM for immediate assistance</p>
              </div>
            </div>

            {/* Credentials Card */}
            <div className="bg-gray-800 rounded-2xl p-4 sm:p-6 border border-gray-700">
              <div className="text-center space-y-3 sm:space-y-4">
                <div className="flex justify-center space-x-4 sm:space-x-6">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-red-400 mb-1">25+</div>
                    <div className="text-xs text-gray-400">Years Experience</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-red-400 mb-1">Level 2</div>
                    <div className="text-xs text-gray-400">Certified</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-red-400 mb-1">Licensed</div>
                    <div className="text-xs text-gray-400">Lic# 13121</div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 leading-relaxed px-2">
                  All loans subject to approval. Licensed mortgage professional serving Waterloo, Brant County, and Wellington County.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
